import { ButtonV2, Spinner } from '@cimplico/components';
import { useFirmEntryDescriptions } from '../../../../queries/use-firm-entry-descriptions';
import { useFirmNotApplicableDescriptions } from '../../../../queries/use-firm-not-applicable-descriptions';
import { useNavigate } from 'react-router-dom';
import { useFirmContext } from '../../../../providers/FirmProvider';
import { useIsFirmRole } from '../../../../hooks/use-is-firm-role';
import { ArrowUpCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import FirmEntryDescriptions from './FirmEntryDescriptions';
import FirmNotApplicableDescriptions from './FirmNotApplicableDescriptions';
import ErrorSplashScreen from '../../../../pages/ErrorSplashScreen';
import { useHasFeatureAccess } from '../../../../hooks/use-has-feature-access';

function FirmDescriptionsContainer(): JSX.Element {
    const entryDescriptionsQuery = useFirmEntryDescriptions();
    const notApplicableDescriptionsQuery = useFirmNotApplicableDescriptions();
    const canAccessFirmDescriptions = useHasFeatureAccess('firm-descriptions:read');

    if (entryDescriptionsQuery.isLoading || notApplicableDescriptionsQuery.isLoading) {
        return (
            <div className="absolute w-full h-[calc(100vh-56px)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (
        canAccessFirmDescriptions &&
        (entryDescriptionsQuery.isError ||
            !entryDescriptionsQuery.isSuccess ||
            notApplicableDescriptionsQuery.isError ||
            !notApplicableDescriptionsQuery.isSuccess)
    ) {
        return <ErrorSplashScreen title="An error has occurred." />;
    }

    const entryDescriptions = entryDescriptionsQuery.data?.entryDescriptions || [];
    const notApplicableDescriptions = notApplicableDescriptionsQuery.data?.notApplicableDescriptions || [];

    return (
        <div className="grid gap-y-4 relative">
            {!canAccessFirmDescriptions && <Preview />}
            <div className={`${!canAccessFirmDescriptions ? 'blur-sm' : ''}`}>
                <FirmEntryDescriptions entryDescriptions={entryDescriptions} />
            </div>
            <div className={`${!canAccessFirmDescriptions ? 'blur-sm' : ''}`}>
                <FirmNotApplicableDescriptions notApplicableDescriptions={notApplicableDescriptions} />
            </div>
        </div>
    );
}

function Preview(): JSX.Element {
    const navigate = useNavigate();
    const { shortId } = useFirmContext();
    const { isAdmin } = useIsFirmRole();

    return (
        <div className="flex flex-col justify-center items-center absolute z-40 top-1/4 right-1/2 translate-x-1/2 translate-y-1/2 bg-white/90 py-4 px-6 rounded-md shadow-md w-[80%]">
            <LockClosedIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
            <p className="text-gray-600 mb-2 text-center">
                Upgrade to the <b>Professional</b> plan to access this feature
            </p>
            <ButtonV2
                type="primary"
                size="sm"
                label="Upgrade"
                leftIcon={<ArrowUpCircleIcon />}
                onClick={(): void => {
                    navigate(`/${shortId}/settings`);
                }}
                isDisabled={!isAdmin}
            />
            {!isAdmin && (
                <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm">To upgrade, please contact your firm&apos;s administrator</p>
            )}
        </div>
    );
}

export default FirmDescriptionsContainer;
